/**
 * Frame-accurate access to one clip's pixels for offscreen rendering.
 *
 * HARD RULE: EXPORT SEEKS, NEVER PLAYS. Playing is wall-clock; if the encode
 * runs at 12 fps the source silently races ahead and every frame is wrong.
 * Each output frame seeks the element to exactly the source time it needs
 * and waits until that frame is painted. DEV builds assert the element is
 * paused on every seek.
 *
 * Media comes from the IndexedDB blob first (a blob: URL never hits Storage
 * CORS and never taints the canvas), then Storage.
 */

import type { VideoClipRef } from '@canvas/shared-types';
import { abortError } from './exportErrors';
import { DEFAULT_SOURCE_FPS, needsSeek } from './videoClip';
import { resolveMediaBlob } from './videoAssetUpload';

/** A seek that hasn't completed in this long renders whatever frame is showing. */
const SEEK_TIMEOUT_MS = 4000;
const METADATA_TIMEOUT_MS = 15_000;

export interface VideoFrameSource {
  readonly element: HTMLVideoElement;
  /** Resolves only once the frame containing srcMs has actually been painted. */
  seekTo(srcMs: number): Promise<void>;
  dispose(): void;
}

/** Thrown when a clip's media can't be found or decoded; callers fall back to the poster. */
export class VideoMediaUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VideoMediaUnavailableError';
  }
}

/** Resolve on `event`, reject on `error`/abort, resolve anyway after `timeoutMs`. */
function once(
  el: HTMLVideoElement,
  event: 'loadedmetadata' | 'seeked' | 'loadeddata',
  signal: AbortSignal | undefined,
  timeoutMs: number
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    const done = (ok: boolean, err?: unknown) => {
      el.removeEventListener(event, onEvent);
      el.removeEventListener('error', onError);
      signal?.removeEventListener('abort', onAbort);
      window.clearTimeout(timer);
      if (err) reject(err);
      else resolve(ok);
    };
    const onEvent = () => done(true);
    const onError = () => done(false, new VideoMediaUnavailableError('This video can’t be decoded in this browser.'));
    const onAbort = () => done(false, abortError());
    const timer = window.setTimeout(() => done(false), timeoutMs);
    el.addEventListener(event, onEvent);
    el.addEventListener('error', onError);
    signal?.addEventListener('abort', onAbort);
  });
}

/**
 * 'seeked' fires BEFORE the new frame is necessarily composited, so gate on
 * requestVideoFrameCallback; without it, two rAFs is the best available proxy.
 */
function painted(el: HTMLVideoElement): Promise<void> {
  const rvfc = (el as HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number;
  }).requestVideoFrameCallback;
  if (typeof rvfc === 'function') {
    return new Promise((resolve) => {
      // rVFC never fires if the frame didn't change (seek to the same frame).
      const timer = window.setTimeout(resolve, 250);
      rvfc.call(el, () => {
        window.clearTimeout(timer);
        resolve();
      });
    });
  }
  return new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

/** MediaRecorder WebM reports an endless duration until a seek past the end. */
async function settleMediaDuration(el: HTMLVideoElement, signal?: AbortSignal): Promise<void> {
  if (Number.isFinite(el.duration) && el.duration > 0 && el.duration < 1e7) return;
  try {
    el.currentTime = 1e101;
  } catch {
    return;
  }
  await once(el, 'seeked', signal, 2500);
  try {
    el.currentTime = 0;
  } catch {
    return;
  }
  await once(el, 'seeked', signal, 2500);
}

export async function createVideoFrameSource(args: {
  clip: VideoClipRef;
  signal?: AbortSignal;
}): Promise<VideoFrameSource> {
  const { clip, signal } = args;
  const blob = await resolveMediaBlob(clip);
  if (signal?.aborted) throw abortError();
  if (!blob) throw new VideoMediaUnavailableError('A video clip’s media couldn’t be found.');

  const url = URL.createObjectURL(blob);
  const el = document.createElement('video');
  el.preload = 'auto';
  el.muted = true;
  el.playsInline = true;
  // A detached video keeps the first decoded frame. Mount it so later seeks present.
  el.style.cssText = 'position:fixed;left:0;top:0;width:160px;height:90px;opacity:0.02;pointer-events:none;z-index:-1;';
  el.src = url;
  document.body.appendChild(el);

  const release = () => {
    el.remove();
    el.removeAttribute('src');
    // Drops the decoder and its frame buffers now rather than at GC.
    el.load();
    URL.revokeObjectURL(url);
  };

  try {
    const ok = await once(el, 'loadedmetadata', signal, METADATA_TIMEOUT_MS);
    if (!ok || !el.videoWidth) throw new VideoMediaUnavailableError('This video can’t be decoded in this browser.');
  } catch (err) {
    release();
    throw err;
  }
  el.width = el.videoWidth;
  el.height = el.videoHeight;
  await settleMediaDuration(el, signal);

  const fps = clip.fps || DEFAULT_SOURCE_FPS;
  let lastSrcMs: number | null = null;
  let seeks = 0;
  let skipped = 0;
  let disposed = false;

  return {
    element: el,

    async seekTo(srcMs) {
      if (disposed) return;
      if (import.meta.env.DEV && !el.paused) {
        console.error('[videoFrameSource] export source is playing; frames will be wrong');
      }
      if (!needsSeek(lastSrcMs, srcMs, fps)) {
        skipped += 1;
        return;
      }
      seeks += 1;
      lastSrcMs = srcMs;
      const endMs = Number.isFinite(el.duration) && el.duration > 0 ? el.duration * 1000 - 40 : srcMs;
      const target = Math.max(0, Math.min(srcMs, endMs)) / 1000;
      if (Math.abs(el.currentTime - target) > 0.001) {
        el.currentTime = target;
        await once(el, 'seeked', signal, SEEK_TIMEOUT_MS);
      }
      await painted(el);
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      if (import.meta.env.DEV && seeks + skipped > 0) {
        const rate = Math.round((skipped / (seeks + skipped)) * 100);
        console.info(
          `[videoFrameSource] ${clip.clipId}: ${seeks} seeks, ${skipped} deduped (${rate}%) at ${fps} fps source`
        );
      }
      release();
    },
  };
}
